import { useColorScheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { createApiResponse, getStatusCodeFromError, withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { updateClientCode } from "@entrilia/shared/api/clientApiContext";
import { ssoApi } from "@entrilia/shared/api/sso";
import { getAuthSettings } from "@entrilia/shared/auth/settings";
import { useAuth } from "@entrilia/shared/auth/useAuth";
import AuthenticationFailed from "@entrilia/shared/components/AuthenticationFailed";
import ErrorBoundary from "@entrilia/shared/components/ErrorBoundary";
import GlobalNotificationErrorSnackbar from "@entrilia/shared/components/GlobalNotificationErrorSnackbar";
import GlobalNotificationSnackbar from "@entrilia/shared/components/GlobalNotificationSnackbar";
import NoAccessPage from "@entrilia/shared/components/NoAccessPage";
import OutdatedBrowserAlert from "@entrilia/shared/components/OutdatedBrowserAlert";
import FullScreenLoader from "@entrilia/shared/components/fullScreenLoader/FullScreenLoader";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { ClientPermissionsContextProvider } from "@entrilia/shared/contexts/ClientPermissionsContextProvider";
import IntercomContextProvider from "@entrilia/shared/contexts/IntercomContextProvider";
import { NotificationContextProvider } from "@entrilia/shared/contexts/NotificationContextProvider";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { getUserLocaleInfo, setUserLocale } from "@entrilia/shared/utilities/userLocaleConfig";
import Main from "./Main";
import Pages from "./Pages";
import adminApi from "./api/adminApi";
import type { Category } from "./api/types/accessTypes";
import type { UserInfo, UserPermissions } from "./api/types/userTypes";
import { redirectToSignin } from "./auth/authRedirects";
import ConsentAgreementContainer from "./components/consentAgreement/ConsentAgreementContainer";
import { getUnsignedGeneralAgreements } from "./components/consentAgreement/consentAgreementService";
import { ClientContextProvider } from "./context/ClientContext";
import { ResourceChangesContextProvider } from "./context/ResourceChangesContext";
import { UserContextProvider } from "./context/UserContext";
import { VisualContextProvider } from "./context/VisualContextProvider";
import useUserContext from "./context/useUserContext";
import useResolvedColorSchemeMode from "./hooks/useResolvedColorSchemeMode";
import storage from "./storage/storage";

const getAccessCategories = withErrorHandling(adminApi.getAccessCategories);

const BetaThemeGuard = () => {
  const { isBetaAccessEnabled } = useUserContext();
  const { mode, setMode } = useColorScheme();

  useEffect(() => {
    if (!isBetaAccessEnabled && mode !== "light") {
      setMode("light");
    }
  }, [isBetaAccessEnabled, mode, setMode]);

  return null;
};

const App = () => {
  const { client: clientParam } = useParams();
  const resolvedMode = useResolvedColorSchemeMode();

  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [userPermissions, setUserPermissions] = useState<UserPermissions>();
  const [authError, setAuthError] = useState<"unauthorized" | "forbidden">();

  const navigate = useNavigate();

  const onAuthenticated = useCallback(async () => {
    try {
      const identityResp = await adminApi.getIdentity();

      const languageTag = identityResp.data?.userPreferences?.languageTag;
      if (languageTag) {
        setUserLocale(languageTag);
      }

      setUserInfo(identityResp.data);

      if (identityResp.data) {
        const unsignedGeneralAgreements = getUnsignedGeneralAgreements(identityResp.data.consentAgreements ?? []);
        if (unsignedGeneralAgreements.length === 0) {
          const permissionsResp = await adminApi.getUserPermissions();
          setUserPermissions(permissionsResp.data);
        }
      }
    } catch (error) {
      const statusCode = getStatusCodeFromError(error);
      if (statusCode === 401) {
        const { userFlowSignIn, cookieTarget } = getAuthSettings();
        await ssoApi.logout({ userFlow: userFlowSignIn, cookieTarget });
        setAuthError("unauthorized");
      } else if (statusCode === 403) {
        setAuthError("forbidden");
      } else {
        logError(error, "[App] onAuthenticated");
        navigate("/error");
      }
    }
  }, [navigate]);

  const currentClientCode = useMemo(() => (clientParam ? clientParam.toLowerCase() : undefined), [clientParam]);

  const currentClient = useMemo(() => {
    if (userPermissions === undefined) {
      return undefined;
    }

    const currentClientPermissions = userPermissions.permissions.find(
      (p) => p.clientCode.toLowerCase() === currentClientCode
    );

    if (!currentClientPermissions) {
      return undefined;
    }

    updateClientCode(currentClientPermissions.clientCode);

    const currentClientInfo = userPermissions.clients.find((c) => c.clientCode === currentClientPermissions.clientCode);
    if (!currentClientInfo) {
      return undefined;
    }

    return {
      ...currentClientPermissions,
      ...currentClientInfo,
    };
  }, [currentClientCode, userPermissions]);

  const refreshUserData = useCallback(async () => {
    try {
      const resp = await adminApi.getUserPermissions();
      if (resp.data) {
        setUserPermissions(resp.data);
      }
    } catch (error) {
      logError(error, "[App] refreshUserData");
    }
  }, []);

  const getCurrentClientSettings = useCallback(() => {
    if (!currentClient?.clientCode) {
      return Promise.resolve(createApiResponse(undefined));
    }

    return adminApi.getClientSettings();
  }, [currentClient?.clientCode]);

  const [currentClientSettings, fetchClientSettingsError, { isFetching: isFetchingClientSettings }] =
    useFetch(getCurrentClientSettings);

  const getCurrentClientDictionaries = useCallback(() => {
    if (!currentClient?.clientCode) {
      return Promise.resolve(createApiResponse(undefined));
    }

    if (currentClient.type === "FundAdmin") {
      return Promise.resolve(
        createApiResponse({
          countries: [],
          states: [],
          currencies: [],
          taxForms: [],
          investorCategories: [],
          formPfNames: [],
          chapter3Statuses: [],
          chapter4FatcaStatuses: [],
          irs1099Codes: [],
          industriesAndSectors: [],
          geographies: [],
        })
      );
    }

    return adminApi.getClientDictionaries();
  }, [currentClient]);

  const [currentClientDictionaries, fetchClientDictionariesError, { isFetching: isFetchingClientDictionaries }] =
    useFetch(getCurrentClientDictionaries);

  const getCurrentAccessCategories = useCallback(async () => {
    if (!currentClient?.clientCode || currentClient.type === "FundAdmin") {
      return createApiResponse<Category[]>([]);
    }

    const [categories, error] = await getAccessCategories();
    if (error && !error.isCanceledRequest) {
      logError(error, "[App] getAccessCategories");
    }

    return createApiResponse(categories ?? []);
  }, [currentClient]);

  const [accessCategories, , { fetch: refreshAccessCategories }] = useFetch(getCurrentAccessCategories);

  const getMenuBadges = useCallback(() => {
    if (!currentClient?.clientCode || currentClient.type === "FundAdmin") {
      return Promise.resolve(createApiResponse(undefined));
    }

    return adminApi.getMenuBadges();
  }, [currentClient]);

  const [menuBadges, , { fetch: fetchMenuBadges }] = useFetch(getMenuBadges);

  const isAuthenticated = useAuth(onAuthenticated, redirectToSignin);

  if (!isAuthenticated) {
    return <FullScreenLoader title="Please wait" subtitle="You are being redirected for authentication..." />;
  }

  if (authError === "unauthorized") {
    return <AuthenticationFailed />;
  }

  if (authError === "forbidden") {
    return <NoAccessPage userEmail={userInfo?.email} onSignInWithAnotherAccountClick={redirectToSignin} />;
  }

  if (fetchClientSettingsError ?? fetchClientDictionariesError) {
    logError(fetchClientSettingsError ?? fetchClientDictionariesError, "[App] clientSettings");
    return <Navigate to="/error" />;
  }

  if (userInfo === undefined) {
    return <InlineLoader text="Loading user data..." />;
  }

  const consentAgreementsToAccept = getUnsignedGeneralAgreements(userInfo.consentAgreements ?? []);
  if (consentAgreementsToAccept.length > 0) {
    return <ConsentAgreementContainer consentAgreements={consentAgreementsToAccept} />;
  }

  if (userPermissions === undefined) {
    return <InlineLoader text="Loading user data..." />;
  }

  const currentClientPermissions = userPermissions.permissions.find(
    (p) => p.clientCode.toLowerCase() === currentClientCode
  );

  if (!currentClientPermissions && userPermissions.permissions.length > 0) {
    const clientCodes = userPermissions.permissions.map((p) => p.clientCode);
    const recentClientCode = storage.getRecentClients().find((c) => clientCodes.includes(c));
    const selectedClientCode = recentClientCode ?? clientCodes[0] ?? "";
    return <Navigate to={`/${selectedClientCode}`} />;
  }

  if (
    currentClient === undefined ||
    currentClientSettings === undefined ||
    currentClientDictionaries === undefined ||
    accessCategories === undefined ||
    isFetchingClientSettings ||
    isFetchingClientDictionaries
  ) {
    return <InlineLoader text="Loading client data..." />;
  }

  return (
    <>
      <OutdatedBrowserAlert />
      <ErrorBoundary>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getUserLocaleInfo().locale}>
          <UserContextProvider
            userInfo={userInfo}
            permissions={userPermissions.permissions}
            userClients={userPermissions.clients}
            refreshUserData={refreshUserData}
          >
            <BetaThemeGuard />
            <IntercomContextProvider
              fullName={userInfo.name}
              email={userInfo.email}
              settings={currentClientSettings.intercomAuthentication}
              themeMode={resolvedMode}
            >
              <ClientContextProvider
                clientSettings={currentClientSettings}
                clientDictionaries={currentClientDictionaries}
                accessCategories={accessCategories}
                refreshAccessCategories={refreshAccessCategories}
                menuBadges={menuBadges}
                updateMenuBadges={fetchMenuBadges}
                client={currentClient}
              >
                <ResourceChangesContextProvider>
                  <ClientPermissionsContextProvider
                    permissions={currentClient.permissions}
                    permissionSets={userPermissions.permissions}
                  >
                    <NotificationContextProvider>
                      <VisualContextProvider>
                        <Main>
                          <Pages />
                        </Main>
                      </VisualContextProvider>
                      <GlobalNotificationSnackbar />
                      <GlobalNotificationErrorSnackbar />
                    </NotificationContextProvider>
                  </ClientPermissionsContextProvider>
                </ResourceChangesContextProvider>
              </ClientContextProvider>
            </IntercomContextProvider>
          </UserContextProvider>
        </LocalizationProvider>
      </ErrorBoundary>
    </>
  );
};

export default App;
